"use client"

import * as React from "react"
import { format } from "date-fns"
import { CalendarIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "../../lib/utils"
import { Button } from "./button"
import { Calendar } from "./calendar"
import { Label } from "./label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover"

export interface DatePickerProps {
  value?: Date
  onChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  label?: string
  /**
   * Renders the shared required marker on the label and sets `aria-required`
   * on the trigger. Like every picker, the marker is LABEL-GATED: with no
   * `label` prop the visible label is a sibling `<Label>` the caller owns, and
   * that is the one to mark.
   */
  required?: boolean
  /**
   * Inline error message. Renders below the trigger and is associated with it
   * via `aria-describedby`, so a screen reader announces it as part of the
   * field rather than as an unrelated paragraph. Implies `invalid`.
   */
  error?: string
  /** Marks the control invalid with no message of its own (a form whose rule is `required: true`). */
  invalid?: boolean
  /** Custom display formatter for the selected date. Falls back to date-fns format "PPP". */
  formatDisplay?: (date: Date) => string
}

export function DatePicker({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  label,
  required,
  error,
  invalid,
  formatDisplay,
}: DatePickerProps) {
  const { t } = useTranslation("common")
  const reactId = React.useId()
  const triggerId = `${reactId}-trigger`
  const errorId = `${reactId}-error`
  const placeholderText = placeholder ?? t("app.pickADate")
  const isInvalid = Boolean(error) || Boolean(invalid)

  const picker = (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          id={triggerId}
          // `combobox`, not the implicit `button`: the ARIA `button` role
          // supports neither `aria-required` nor `aria-invalid`, so on a plain
          // button the two attributes below are dropped by assistive tech (and
          // rejected by `jsx-a11y/role-supports-aria-props`). Radix's
          // PopoverTrigger already supplies the `aria-expanded`/`aria-controls`
          // the role requires — the same shape shadcn's own SelectTrigger uses.
          // eslint-disable-next-line jsx-a11y/role-has-required-aria-props -- aria-expanded/aria-controls are injected at runtime by Radix's PopoverTrigger (asChild), which the static rule cannot see
          role="combobox"
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground",
            isInvalid && "border-destructive",
            className
          )}
          disabled={disabled}
          aria-required={required || undefined}
          aria-invalid={isInvalid || undefined}
          aria-describedby={error ? errorId : undefined}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? (formatDisplay ? formatDisplay(value) : format(value, "PPP")) : <span>{placeholderText}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={onChange}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )

  if (!label && !error) {
    return picker
  }

  return (
    <div className="space-y-2">
      {label && (
        <Label htmlFor={triggerId} required={required}>
          {label}
        </Label>
      )}
      {picker}
      {error && (
        // `text-sm`, the tree's dominant field-error size (312 sites to 77), so
        // a date's message matches the Input and Select messages beside it in
        // the same dialog rather than reading a step smaller than its siblings.
        <p id={errorId} className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  )
}
